import { ArrowUp, Flame, Loader2, Moon, Sun, Thermometer } from 'lucide-react'
import React, { FC, ReactNode, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'

const HotbarContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0px;
`

const LayerButton = styled.div<{ $selected: boolean }>`
  cursor: pointer;
  background-color: ${({ $selected }) =>
    $selected ? 'rgba(80, 80, 100, 0.9)' : 'rgba(20, 20, 30, 0.8)'};
  padding: 8px 12px;
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:first-child {
    border-radius: 6px 0 0 6px;
  }

  &:last-child {
    border-radius: 0 6px 6px 0;
  }

  &:only-child {
    border-radius: 6px;
  }

  &:hover {
    background-color: ${({ $selected }) =>
      $selected ? 'rgba(80, 80, 100, 1)' : 'rgba(40, 40, 50, 0.9)'};
  }

  /* Custom tooltip - appears below to avoid container clipping */
  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.15s,
      visibility 0.15s;
    pointer-events: none;
    z-index: 1000;
  }

  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
`

const LayerText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 10px;
  color: white;
  text-transform: uppercase;
`

const LayerIconWrapper = styled.div`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const IndexBadge = styled.div`
  position: absolute;
  bottom: 2px;
  right: 4px;
  font-size: 9px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.6);
  pointer-events: none;
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;

  svg {
    animation: ${spin} 1s linear infinite;
  }
`

interface ReadOnlyLayerHotbarProps {
  availableLayers: string[]
  activeLayer: string
  loadingLayer?: string | null
  onLayerChange: (layer: string) => void
}

const KEYWORD_ICON_MAP: Record<string, ReactNode> = {
  ironbow: <Flame size={18} />,
  height: <ArrowUp size={18} />,
  thermal: <Thermometer size={18} />,
  day: <Sun size={18} />,
  night: <Moon size={18} />,
}

function getLayerIcon(layerName: string): ReactNode | null {
  const lower = layerName.toLowerCase()
  for (const keyword of Object.keys(KEYWORD_ICON_MAP)) {
    if (lower.includes(keyword)) {
      return KEYWORD_ICON_MAP[keyword]
    }
  }
  return null
}

/** Returns sort priority: RGB first, Day IR second, Night IR third, others last */
function getLayerSortOrder(layer: string): number {
  const lower = layer.toLowerCase()
  if (lower.includes('rgb')) return 0
  if (lower.includes('day')) return 1
  if (lower.includes('night')) return 2
  return 3
}

export const ReadOnlyLayerHotbar: FC<ReadOnlyLayerHotbarProps> = ({
  availableLayers,
  activeLayer,
  loadingLayer,
  onLayerChange,
}) => {
  // Sort layers: RGB, Day IR, Night IR, others
  const sortedLayers = [...availableLayers].sort(
    (a, b) => getLayerSortOrder(a) - getLayerSortOrder(b)
  )

  // Keyboard shortcuts (1-9)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target && target.isContentEditable)
      ) {
        return
      }

      const key = event.key
      if (key >= '1' && key <= '9') {
        const idx = Number(key) - 1
        if (idx < sortedLayers.length) {
          onLayerChange(sortedLayers[idx])
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [sortedLayers, onLayerChange])

  if (sortedLayers.length <= 1) {
    return null
  }

  const getLayerLabel = (layer: string): string => {
    const lower = layer.toLowerCase()
    if (lower.includes('rgb')) return 'RGB'
    if (lower.includes('thermal')) return 'THM'
    if (lower.includes('height')) return 'HGT'
    if (lower.includes('ironbow')) return 'IRN'
    if (lower.includes('night')) return 'NIGHT'
    if (lower.includes('day')) return 'DAY'
    if (layer.length <= 5) return layer.toUpperCase()
    return layer.substring(0, 4).toUpperCase()
  }

  const getLayerTooltip = (layer: string, index: number): string => {
    const lower = layer.toLowerCase()
    let description = layer
    if (lower.includes('rgb')) description = 'RGB Color'
    else if (lower.includes('thermal')) description = 'Thermal'
    else if (lower.includes('height')) description = 'Height Map'
    else if (lower.includes('ironbow')) description = 'Ironbow Thermal'
    else if (lower.includes('night')) description = 'Night View'
    else if (lower.includes('day')) description = 'Day View'
    return `Switch to ${description} layer (${index + 1})`
  }

  const renderLayerContent = (layer: string): ReactNode => {
    const icon = getLayerIcon(layer)
    if (icon) {
      return <LayerIconWrapper>{icon}</LayerIconWrapper>
    }
    return <LayerText>{getLayerLabel(layer)}</LayerText>
  }

  return (
    <HotbarContainer>
      {sortedLayers.map((layer, index) => {
        const isLoading = layer === loadingLayer
        return (
          <LayerButton
            key={layer}
            $selected={layer === activeLayer}
            onClick={() => onLayerChange(layer)}
            data-tooltip={getLayerTooltip(layer, index)}>
            {renderLayerContent(layer)}
            <IndexBadge>{index + 1}</IndexBadge>
            {isLoading && (
              <LoadingOverlay>
                <Loader2 size={16} />
              </LoadingOverlay>
            )}
          </LayerButton>
        )
      })}
    </HotbarContainer>
  )
}
